import { ButtonRedirection, CameraOverlayButton, HeaderPopup, Icons, LayoutType, MicrophoneOverlayButton, Popup, ReactIcons, StudentPage, StudentUI } from "@kalyzee/escaet-ui";
import { Outlet } from "react-router-dom";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import {
  generateRandomId,
  isSpectator,
  MeetingSessionOvenMediaReceiver,
  MeetingSessionOvenMediaReceiverManagerBaseData,
  MeetingSourceDeviceType,
  useMeetingAudioMixer,
} from "@kalyzee/kast-web-sdk";
import {
  addComponentOnOverlay,
  audioContextIsReadyToUse,
  awaitWithMinimumDelay,
  Button,
  delay,
  Loading,
  MediaCaptureRef,
  removeComponentOnOverlay,
  Touchable,
  useOuterClickManyTargets,
  useRefAutoUpdate,
  useStateWithRef,
  Video,
} from "@kalyzee/kast-web-sdk/web-components";
import { Chatroom, MeetingRoomStatus, MeetingSessionAddMedia, MeetingSessionHighlightingState, MeetingSessionStatus, MeetingState, MeetingUpdateByIdResult, UserRole } from "@kalyzee/kast-web-sdk/websocket";
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MediaSelector } from "../../../../components/media/MediaSelector";
import { toastError, toastSuccess } from "../../../../helpers/toast";
import { MeetingSourceExtended, useMeetingConfigurator, useMeetingDataOnChange, useMeetingSelector, useMeetingSources, useMultiMeetingAPI } from "../../../../hooks/meeting";
import { useStudentContext } from "../context";

import { OvenMediaReceiverWebRTCSession, RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import { Highlighting } from "../../../../components/media/Highlighting";
import MeetingSessionMediaPlayer from "../../../../components/player/MeetingSessionMediaPlayer";
import { getExpertActor, isExpertVideoSession } from "../../../../helpers/meeting";

import PauseView from "../../../../components/utils/PauseView";
import { useSettings } from "../../../../hooks/settings";

import { BottomPanelWebrtcSessionStats } from "../../../../components/debug/BottonPanelWebrtcSessionStats";
import { ScreenShare, ScreenShareImperativeAPI, ScreenShareStatus } from "../../../../components/media/ScreenShare";
import { MeetingActivityJoinPopup } from "../../../../components/meeting/MeetingActivityJoinPopup";
import { DevSoundIndicatorsBanner } from "../../../../components/utils/DevSoundIndicatorBanner";
import OverlayInformationMessage from "../../../../components/utils/OverlayInformationMessage";
import { useActivityData, useBroadcastingExpertSessions, usePopulatedMeetingData, useStudentAvailableViews } from "../../../../hooks/populatedData";

import { MeetingContainer, MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import SelfPreview from "../../../../components/media/SelfPreview";
import { getAudioContextForAudioMixer, getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { sdkClient } from "../../../../helpers/sdk";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";
import { expertWebcamName, pedagogyWebcamName, studentWebcamName } from "../../../../helpers/webcam";
import { useScoreReporter } from "../../../../hooks/behaviors";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { BoxContainer, BoxOverlay, BoxOverlayProps, CircleIcon, OverlayContainer, SecondarySideBar, Spacer, SquareIcon, TopBar, VideoCallControls, VideoCallControlsStudent } from "@kalyzee/kalyzee-ui";
import { BoardInputsWorkBox, InputsWorkBoxProps } from "../../../../components/workBoxes/BoardInputsWorkBox";
import { ChatWorkBox } from "../../../../components/workBoxes/ChatWorkBox";
import { MasterUI } from "../../../../components/common/MasterUI";
import { ROLE } from "@kalyzee/kast-web-sdk/http";
import { isExpert, isRoleGreaterThanOrEqual } from "../../../../helpers/roles";
import { addPopup, removePopup, removePopupByGroup } from "../../../../helpers/popup";
import { MediaSourcePicker } from "../../../../components/common/MediaSourcePicker";
import { MediaSourceViewer } from "../../../../components/common/MediaSourceViewer";
import { SettingsWorkBox } from "../../../../components/workBoxes/SettingsWorkBox";

import styles from "./meeting.module.css";
import { useMeetingMasterMessagingBridge } from "../../../../hooks/secondaryScreens/messagingBridge";
import Chat, { DashboardChatRef } from "../../../../components/chat/Chat";
import { useBoardMedia } from "../../../../hooks/board";
import { AppSoundSlider } from "../../../../components/utils/AppSoundSlider";
import clsx from "clsx";
import { set } from "lodash";

const fromUIPage = (page: StudentPage) => {
  const mapping = {
    [StudentPage.Dashboard]: PageMasterMode.MEETING_DASHBOARD,
    [StudentPage.Group]: PageMasterMode.MEETING_GROUP,
    [StudentPage.Highlight]: PageMasterMode.MEETING_HIGHLIGHT,
    [StudentPage.Survey]: PageMasterMode.MEETING_SURVEY,
  };
  return mapping[page];
};

const toUIPage = (page?: PageMasterMode) => {
  if (page === PageMasterMode.MEETING_DASHBOARD) return StudentPage.Dashboard;
  if (page === PageMasterMode.MEETING_GROUP) return StudentPage.Group;
  if (page === PageMasterMode.MEETING_HIGHLIGHT) return StudentPage.Highlight;
  if (page === PageMasterMode.MEETING_SURVEY) return StudentPage.Survey;
  return StudentPage.Dashboard;
};

export const MeetingStudentPage = () => {
  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);

  const highlightMode = useRef(false);
  const surveyMode = useRef(false);

  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const screenShareRef = useRef<ScreenShareImperativeAPI>(null);

  const [screenShareStatus, setScreenShareStatus] = useState<ScreenShareStatus>("idle");

  const selfUser = useAppStoreSelector(state => state.user);

  const [overlayMode, setOverlayMode, overlayModeRef] = useStateWithRef<"none" | "media" | "screen">("none");

  const multiMeetingAPI = useMultiMeetingAPI();
  const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);

  // If a Peda Meeting API is available, we use it and it becomes the focusedMeetingAPI
  // Otherwise, we use the URL selected meeting API
  const { selectedMeetingAPI, pedagogy, focusedMeetingAPI } = useMultiMeetingAPI(
    pageContextRef.current?.params?.id,
    true,
    true // only selected if no other running
  );
  const focusedMeetingAPIRef = useRefAutoUpdate(focusedMeetingAPI);

  const [showEndMeetingConfirmationPopup, setShowEndMeetingConfirmationPopup] = useState(false);

  const autojoin = pageContextRef.current?.searchParams?.get("autojoin") === "true";
  
  (window as any).focusedMeetingAPI = focusedMeetingAPI;
  useMeetingConfigurator(focusedMeetingAPI, {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.name === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.screensharing ?? DEFAULT_MAX_BITRATES.student.screensharing;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
                },
              },
            };
          }
          const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.webcam ?? DEFAULT_MAX_BITRATES.student.webcam;
          return {
            rtc: {
              streamParams: {
                preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
              },
            },
          };
        },
      },
      reception: {
        options: () => ({
          video: { mode: "subscription", delayToDisableStream: 5000 },
          audio: true,
        }),
      },
    },
  });

  const [meeting] = useMeetingSelector(focusedMeetingAPI, (state) => state.meeting);
  const [session, sessionRef] = useMeetingSelector(focusedMeetingAPI, (state) => state.session);
  const [rooms] = useMeetingSelector(focusedMeetingAPI, (state) => state.rooms);
  const [emissionSessions] = useMeetingSelector(focusedMeetingAPI, (state) => state.emissionSessions);
  const [receptionSessions] = useMeetingSelector(focusedMeetingAPI, (state) => state.receptionSessions);
  const [currentSurvey] = useMeetingSelector(focusedMeetingAPI, (state) => state.surveys?.[0]);

  const [selectedSession] = useMeetingSelector(selectedMeetingAPI, (state) => state.session);

  const studentCtx = useStudentContext();
  const studentCtxRef = useRefAutoUpdate(studentCtx);

  const mediaSelectorRef = useRef<MediaCaptureRef>();
 
  const chatRef = useRef<DashboardChatRef>(null);

  const [audioPermitted] = useMeetingSelector(focusedMeetingAPI, (state) => state.session?.permissions?.audio ?? true);

  const [workBoxMode, setWorkBoxMode] = useState<"none" | "inputs" | "requests" | "chat">("none");

  const isStoppingRef = useRef(false);

  const sources = useMeetingSources();

  const usableSources: MeetingSourceExtended[] = useMemo(() => {
    return isSpectator(session?.role) ? [] : ((sources ?? []) as any);
  }, [sources, session?.role]);

  const boardLocalOrRemoteMedia = useBoardMedia(focusedMeetingAPI);
  
  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!studentCtx || !meeting) return { enabledMedia: [] };
    const studentMeetingOptions = studentCtx.getMeetingOptions(meeting.id);

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => !enabledVideoSessionsIds?.includes(media.mediaSession.id));
    
    return { enabledMedia };
  }, [meeting, boardLocalOrRemoteMedia, studentCtx]);

  useMeetingMasterMessagingBridge();

  (window as any).studentCtx = studentCtx;

  useMeetingDataOnChange(
    focusedMeetingAPI,
    (state) => state?.session?.permissions?.audio ?? true,
    (value, previous) => {
      if (value) toastSuccess("Vous pouvez à nouveau activer votre micro.");
      else toastError("L'activation du micro est désormais bloquée.");
    }
  );

  useEffect(() => {
    const emissionManager = focusedMeetingAPI?.emissionManager;
    if (emissionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (emissionManager.paused) emissionManager.restore();
      } else {
        if (!emissionManager.paused) emissionManager.pause();
      }
    }
    const receptionManager = focusedMeetingAPI?.receptionManager;
    if (receptionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (receptionManager.paused) receptionManager.restore();
      } else {
        if (!receptionManager.paused) receptionManager.pause(); 
      }
    }
  }, [focusedMeetingAPI, meeting?.state])

  /**
   * Switching between paused and running when a pedagogy is available for us
   */
  useEffect(() => {
    if (!selectedSession) return;

    const status = selectedSession?.status;
    const emitter = sdkClient.getWebsocketClient().emitter;

    const waitedInPedagogy = !!pedagogy?.myRoom;
    if (waitedInPedagogy && status === MeetingSessionStatus.RUNNING) {
      // A peda meeting is available but we need to pause this meeting in
      selectedMeetingAPI?.updater.update(selectedSession.id, {
        status: MeetingSessionStatus.PAUSED,
      });
    }

    if (!waitedInPedagogy && status === MeetingSessionStatus.PAUSED) {
      // No peda meeting available, we can unpause the current meeting
      selectedMeetingAPI?.updater.update(selectedSession.id, {
        status: MeetingSessionStatus.RUNNING,
      });
    }
  }, [pedagogy, selectedMeetingAPI, selectedSession]);

  /**
   * If we are connected to a pedagogy meetign but we no longer
   * have a room waiting for us in it, we need to leave it
   */
  useEffect(() => {
    if (pedagogy && !pedagogy.myRoom) {
      pedagogy.container.getAPI().leave();
    }
  }, [pedagogy]);

  ////////////////////// Populated DATA //////////////////////

  const populatedMeetingData = usePopulatedMeetingData(focusedMeetingAPI);
  const activityData = useActivityData(populatedMeetingData);

  const availableViews = useStudentAvailableViews(populatedMeetingData, activityData, selfUser?.id, session?.id);

  const currentView = useMemo(() => {
    if (pageContext?.page === PageMasterMode.MEETING_HIGHLIGHT) {
      const highlightView = availableViews.find((view) => view.type === "highlight");
      if (highlightView) return highlightView;
    }
    return availableViews[availableViews.length - 1]; // TODO : there should be a default view, like this structure : { availableViews, defaultView, currentView }
  }, [availableViews, pageContext]);

  const broadcastingExpertSessions = useBroadcastingExpertSessions(populatedMeetingData);

  const expertNonWebcamVideoSessions = useMemo(
    () =>
      (currentView?.experts ?? [])
        .flatMap((data) => data.session.videoSessions)
        .filter((videoSession) => videoSession.data.mediaSession.name !== expertWebcamName),
    [currentView]
  );

  ////////////////////// Work Boxes DATA //////////////////////

  useEffect(() => {
    if (!session || !focusedMeetingAPIRef.current) return;
    const highlightView = availableViews.find((view) => view.type === "highlight");
    if (!highlightView) return;

    // My session is highlighted and confirmed (video:true)
    const mySessionData = highlightView.students.find((s) => s.video && s.session.id === session.id);

    if (mySessionData && !focusedMeetingAPIRef.current?.emissionManager.audioIsEnabled("cameras")) {
      focusedMeetingAPIRef.current?.unmute({ direction: "outgoing", group: "cameras" });
    }
  }, [session, availableViews])

  const canHearSession = (_: MeetingContainerAPI, session: MeetingSessionOvenMediaReceiver) => {
    return true; // everyone hears everyone, no group mode for now
  };

  const audioMixer = useMeetingAudioMixer(getAudioContextForAudioMixer(), focusedMeetingAPI, true, canHearSession, settings.debug?.audio?.mixer);

  const myGroupStatus = populatedMeetingData?.me.group?.room.status;
  const canShareScreen = session?.highlighting === MeetingSessionHighlightingState.ONGOING || (myGroupStatus && myGroupStatus !== MeetingRoomStatus.Closed);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  /**
   * Joins a container, not necessarily the current one
   */
  const joinContainer = async (container: MeetingContainer) => {
    if (container.getStatus() !== "disconnected") return;
    const containerAPI = container.getAPI();
    if (studentCtx) {
      if (studentCtx.audioStreamEnabled) await containerAPI.unmute({ direction: "outgoing", group: "cameras" });
      else await containerAPI.mute({ direction: "outgoing", group: "cameras" });
      if (studentCtx.videoStreamEnabled) await containerAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      else await containerAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      studentCtx.setAudioStreamEnabled(containerAPI.emissionManager.audioIsEnabled("cameras"));
      studentCtx.setVideoStreamEnabled(containerAPI.emissionManager.videoIsEnabled("cameras"));
    }

    const medias: MeetingSessionAddMedia<any, any, any>[] = (sources as MeetingSourceExtended[]).map((s) => {
      const type = s.extra.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      return {
        video: {
          deviceType: s.devices[0].type,
          enabled: containerAPI.emissionManager.videoIsEnabled(group),
        },
        audio: {
          deviceType: s.devices[0].type,
          enabled: containerAPI.emissionManager.audioIsEnabled(group),
        },
        name: s.name,
        deviceId: s.id,
        extra: {
          type,
          label: s.name,
          controls: s.controls?.type,
        },
      };
    });

    const result = await containerAPI.join({
      medias,
    });

    if (result.error) {
      console.error(`[ERROR] join: `, result.error);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      container.destroy();
      return;
    }
    const session = result.response?.session;
    if (!session) {
      console.error(`[ERROR] join - internal error: session is undefined`);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      container.destroy();
      return;
    }
  };

  const createAndJoinContainer = async (id: string) => {
    const api = multiMeetingAPIRef.current;

    if (!api.all) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      pageContextRef.current?.goTo(PageMasterMode.LIST);
      return;
    }

    const container = api.all.getOrCreateContainer(id);
    joinContainer(container);
  };

  const leave = async () => {
    // leave all
    const promises: Promise<any>[] = [];
    multiMeetingAPIRef.current?.all?.getContainers()?.forEach((c) => {
      const result = c.getAPI()?.leave();
      if (!("error" in result)) promises.push(result);
    });
    await Promise.allSettled(promises);
  };

  const updateMedias = useCallback(async () => {
    const meetingContainerAPI = focusedMeetingAPIRef.current;
    if (!meetingContainerAPI) return;
    const manager = meetingContainerAPI.emissionManager;
    if (session) {
      let camerasVideoIsEnabled = true;
      let camerasAudioIsEnabled = true;
      session.medias?.forEach((m) => {
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasVideoIsEnabled = camerasVideoIsEnabled && m.video.enabled;
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasAudioIsEnabled = camerasAudioIsEnabled && m.audio.enabled;
      });
      if (!camerasVideoIsEnabled && manager.videoIsEnabled("cameras")) {
        await meetingContainerAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      } else if (camerasVideoIsEnabled && !manager.videoIsEnabled("cameras")) {
        await meetingContainerAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      }
      if (!camerasAudioIsEnabled && manager.audioIsEnabled("cameras")) {
        await meetingContainerAPI.mute({ direction: "outgoing", group: "cameras" });
      } else if (camerasAudioIsEnabled && !manager.audioIsEnabled("cameras")) {
        await meetingContainerAPI.unmute({ direction: "outgoing", group: "cameras" });
      }
    }

    const state = meeting?.state;
    if (state === MeetingState.PAUSED) {
      audioMixer?.suspend().catch((err) => console.error("Error suspend audio : ", err));
    } else {
      audioMixer?.resume().catch((err) => console.error("Error resume audio : ", err));
    }

    studentCtxRef.current?.setVideoStreamEnabled(manager.videoIsEnabled("cameras"));
    studentCtxRef.current?.setAudioStreamEnabled(manager.audioIsEnabled("cameras"));
  }, [studentCtxRef, session, meeting]);

  const onLayoutChange = (type: LayoutType) => {
    studentCtx?.setLayout?.(type);
  };

  const onPageChange = (page: StudentPage) => {
    pageContextRef.current?.goTo(fromUIPage(page), { id: pageContextRef.current?.params?.id });
  };

  // ------------------ EXPERT UTILS ------------------ //

  const updateMeetingState = async (state: MeetingState) => {
    pageContextRef.current?.setLoading(true);
    let result: MeetingUpdateByIdResult | undefined;
    try {
      result = await focusedMeetingAPIRef.current?.updateMeetingState(state);
    } catch (err) {
    } finally {
      pageContextRef.current?.setLoading(false);
    }
    return result;
  };

  const start = async (): Promise<boolean> => {
    isStoppingRef.current = true;
    pageContextRef.current?.setLoading(true);
    const result = await awaitWithMinimumDelay(updateMeetingState(MeetingState.RUNNING), 500);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.RUNNING) {
      return true;
    }
    isStoppingRef.current = false;
    toastError("Une erreur est apparue.");
    return false;
  };

  const stop = async (): Promise<boolean> => {
    // pageContextRef.current?.setLoading(true);
    // await delay(200);
    setShowEndMeetingConfirmationPopup(true);
    //const result = await updateMeetingState(MeetingState.STOPPED);
    // pageContextRef.current?.setLoading(false);
    // if (result && !result.error && result.response?.meeting?.state === MeetingState.STOPPED) {
    //   pageContextRef.current?.goTo(PageMasterMode.LIST);
    //   return true;
    // }
    // toastError("Une erreur est apparue.");
    return false;
  };

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    const containers = multiMeetingAPIRef.current.all?.getContainers();
    containers?.forEach((container) => {
      joinContainer(container);
    });
  }, [multiMeetingAPI.all]);

  useEffect(() => {
    if (!canShareScreen) {
      screenShareRef.current?.stop();
    }
  }, [canShareScreen]);

  useEffect(() => {
    if (screenShareStatus === "idle" && overlayMode === "screen") {
      setOverlayMode("none");
    }
  }, [screenShareStatus, overlayMode]);

  useEffect(() => {
    studentCtx?.setPopulatedMeetingData?.(populatedMeetingData);
    studentCtx?.setAvailableViews?.(availableViews);
    studentCtx?.setCurrentView?.(currentView);
  }, [studentCtx, populatedMeetingData, availableViews, currentView]);

  //const haveActiveGroups = useMemo(() => populatedMeetingData?.rooms?.groups.some((g) => g.room.status !== MeetingRoomStatus.Closed), [populatedMeetingData]);

  useEffect(() => {
    // Not ready to use, we cannot decide if a redirection is needed
    if (!meeting) return;

    // I am in a pedagogy meeting
    if (meeting.specializedData?.isPedagogy) {
      if (pageContextRef.current?.page === PageMasterMode.MEETING_PEDAGOGY) {
        return;
      }
      // I keep the ID of my current meeting, this will be used as an origin meeting
      // => to know what meeting i'm coming from
      pageContextRef.current?.goTo(PageMasterMode.MEETING_PEDAGOGY, { id: pageContextRef.current?.params?.id });
      return;
    }

    if (currentSurvey) {
      // already on page
      if (pageContextRef.current?.page === PageMasterMode.MEETING_SURVEY) {
        return;
      }
      if (surveyMode.current) {
        // We already saw the last survey
        return;
      }
      surveyMode.current = true;
      pageContextRef.current?.goTo(PageMasterMode.MEETING_SURVEY, { id: pageContextRef.current?.params?.id });
      return;
    }
    surveyMode.current = false;

    // Priority to highllighted content
    if (availableViews.find((view) => view.type === "highlight")) {
      if (highlightMode.current) {
        // We already had highlighted sessions (or were highlighted ourself) before this update
        // Maybe we are on the hightlight page, maybe not, does not matter
        return;
      }
      pageContextRef.current?.goTo(PageMasterMode.MEETING_HIGHLIGHT, { id: pageContextRef.current?.params?.id });
      highlightMode.current = true;
      return;
    }
    highlightMode.current = false;

    // Nothing is highlighted, maybe we have active groups
    // if (haveActiveGroups) {
    //   if (pageContextRef.current?.page === PageMasterMode.MEETING_GROUP) {
    //     return;
    //   }
    //   pageContextRef.current?.goTo(PageMasterMode.MEETING_GROUP, { id: pageContextRef.current?.params?.id });
    //   return;
    // }

    // Nothing is highlighted, nothing is active, we can go back to the dashboard
    if (pageContextRef.current?.page !== PageMasterMode.MEETING_DASHBOARD) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: pageContextRef.current?.params?.id });
    }
  }, [availableViews, pageContextRef, populatedMeetingData, currentSurvey]);

  useEffect(() => {
    if (!studentCtx) return;
    if (!mediaSelectorRef.current) return;
    mediaSelectorRef.current.enableAudio(studentCtx.audioStreamEnabled);
    mediaSelectorRef.current.enableVideo(studentCtx.videoStreamEnabled);
  }, [studentCtx]);

  useEffect(() => {
    if (!studentCtx) return;
    const info = studentCtx.outputAudioDeviceInfo;
    if (info && audioMixer?.isRunning()) {
      audioMixer.setAudioOutputDevice(info).catch(() => {
        console.error(`audio device (${JSON.stringify(info)}) is invalid`);
      });
    }
  }, [audioMixer, studentCtx?.outputAudioDeviceInfo]);

  useEffect(() => {
    const id = pageContextRef.current?.params?.id ?? "";
    audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer()).then((ready) => {
      if (!ready) {
        const queries: any = {};
        if (id) queries["meeting"] = id;
        pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries });
      } else {
        createAndJoinContainer(id);
      }
    });
    return () => {
      leave();
    };
  }, []);

  useEffect(() => {
    let currentPopup: React.ReactNode;
    const showMeetingNotReadyPopup = () => {
      if (currentPopup) {
        removeComponentOnOverlay(currentPopup);
        currentPopup = undefined;
      }
      currentPopup = (
        <Popup
          Header={<HeaderPopup icon={Icons.TimerIcon} title="Le cours va bientôt commencer, veuillez patienter" />}
          Content={<Loading style={{ width: "100%", "--loading-svg-color": "black" } as any} />}
          style={{
            width: "fit-content",
            height: "fit-content",
            padding: "2em",
          }}
        />
      );
      addComponentOnOverlay(currentPopup, `meeting_state_not_ready_${generateRandomId()}`);
    };

    const state = meeting?.state;
    if (state === MeetingState.IDLE) {
      showMeetingNotReadyPopup();
    } else {
      if (currentPopup) {
        removeComponentOnOverlay(currentPopup);
        currentPopup = undefined;
      }
    }

    if (state === MeetingState.IDLE) showMeetingNotReadyPopup();

    return () => {
      if (currentPopup) {
        removeComponentOnOverlay(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [meeting?.state]);

  useEffect(() => {
    emissionSessions?.forEach((s) => {
      const type = s.data.mediaSession.extra?.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      if (group) s.data.groups = [group];
    });
  }, [emissionSessions]);

  useEffect(() => {
    receptionSessions?.forEach((s) => {
      s.data.extra = {
        video: isExpertVideoSession(meeting, s),
        audio: true,
      };
    });
  }, [receptionSessions, meeting]);

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  useEffect(() => {
    if (settings.debug?.audio?.mixer?.public) (window as any).audioMixer = audioMixer;
    if (settings.debug?.meetingContext?.public) (window as any).meetingContext = focusedMeetingAPI;
  }, [settings, audioMixer, focusedMeetingAPI]);

  // ------------------ EXPERT EFFECTS ------------------ //

  useEffect(() => {
    const state = meeting?.state;

    let currentPopup: React.ReactNode;
    const endMeetingPopup = () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }

      const stopped = state === MeetingState.STOPPED;

      let buttons: BoxOverlayProps["buttons"];
      
      if (stopped) {
        buttons = [
          {
            label: "Revenir à la liste des cours",
            onClick: () => {
              removePopup(currentPopup);
              removePopupByGroup("main");
              pageContextRef.current?.goTo(PageMasterMode.LIST);
            },
            validateAfter: autojoin ? 10 : undefined,
          }
        ];
        if (isExpert(session)) {
          buttons.push({
            label: "Relancer le cours",
            onClick: async () => {
              await start();
              removePopup(currentPopup);
            }
          });
        }
      } else {
        buttons = [
          {
            label: "Terminer le cours",
            onClick: async () => {
              await updateMeetingState(MeetingState.STOPPED);
            }
          },
          {
            label: "Poursuivre le cours",
            onClick: () => {
              setShowEndMeetingConfirmationPopup(false);
            }
          }
        ]
      }

      currentPopup = (
        <OverlayContainer
          style={{
            width: "100vw",
            height: "100vh",
          }}
          overlay={
            <BoxOverlay
              title={stopped ? "Le cours est terminé" : "Voulez-vous vraiment terminer le cours ?"}
              level={stopped ? "success" : "question"}
              buttons={buttons}
            />
          }
        />
      );
      
      addPopup(currentPopup, "meeting_state_stopped", "main");
    };

    if (state === MeetingState.STOPPED) {
      setShowEndMeetingConfirmationPopup(false);
    }

    if (state === MeetingState.IDLE) {
      start();
    } else if (state === MeetingState.STOPPED || showEndMeetingConfirmationPopup) {
      endMeetingPopup();
    }

    return () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [meeting?.state, showEndMeetingConfirmationPopup]);

  if (!meeting || !session || !rooms || !selfUser || !studentCtx || !focusedMeetingAPI) return <Loading fullscreen message="Chargement en cours" />;

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const renderChatWorkBox = () => {
    let currentChatroom: Chatroom | undefined;

    if (currentView.selfGroup) {
      // I am in a group -> i use the group chatroom
      currentChatroom = currentView.selfGroup.room.chatrooms?.[0];
    } else {
      currentChatroom = meeting.chatrooms?.[0];
    }

    if (!currentChatroom) return null;

    return (
      <ChatWorkBox 
        onClearMessages={isExpert(session) ? () => chatRef.current?.clearMessages() : undefined}
      >
        <Chat 
          chatroom={currentChatroom}
          meetingAPI={focusedMeetingAPI}
          ref={chatRef}
        />
      </ChatWorkBox>
    );
  
    // return (
    //   <DashboardChat
    //     chatroom={currentChatroom}
    //     //style={{ width: 500, height: 400, display: popupMode === "chat" ? "flex" : "none" }}
    //     meetingAPI={focusedMeetingAPI}
    //     className={styles.chatContainer}
    //     hideActiveChatButton
    //     hideClearChatButton
    //     inactive={popupMode !== "chat"}
    //     onUnreadMessages={(messages) => {
    //       setChatBadgeValue(messages.length);
    //     }}
    //     disableDeletion={false}
    //     disableReactions={false}
    //   />
    // );
  };

  const renderWorkBox = () => {
    if (workBoxMode === "none") return null;
    if (workBoxMode  === "inputs") {
      return (
        <div style={{
          flex: 1,
        }}>
          <BoardInputsWorkBox 
            meetingAPI={focusedMeetingAPI}
          />
        </div>
      );
    }
    if (workBoxMode  === "chat") {
      return (
        <div style={{
          flex: 1,
        }}>
          {renderChatWorkBox()}
        </div>
      );
    }
  }
  
  const renderOverlay = () => {
    return (
      <MediaSourcePicker
        meetingAPI={focusedMeetingAPI}
        sources={usableSources} // all sources together, cameras and screens
        display
        className={clsx({
          [styles.mediaSourcePicker]: true,
          [styles.invisible]: overlayMode !== "media",
        })}
      />
    )
  }

  const renderSideBar = () => {
    const top: React.ReactNode[] = [
      <SquareIcon.Variants.ComputerScreen 
        label={"Affichage"}
        selected={workBoxMode === "inputs"}
        onClick={() => setWorkBoxMode((v) => (v === "inputs" ? "none" : "inputs"))}
      />,
    ];
    const bottom: React.ReactNode[] = [
      // <SquareIcon.Variants.Intervention
      //   label="Interventions"
      // />,
      <SquareIcon.Variants.Chat 
        label="Chat"
        selected={workBoxMode === "chat"}
        onClick={() => setWorkBoxMode((v) => (v === "chat" ? "none" : "chat"))}
      />,
    ];
    return (
      <SecondarySideBar 
        top={top}
        bottom={bottom}
      />
    );
  }
 
  const renderBottomBar = () => {
    const left: React.ReactNode[] = [];
    
    if (enabledMedia.length > 1) {
      left.push(
        <div style={{
          display: "flex",
          flexDirection: "row",
          gap: "0.5em",
          alignItems: "center",
          height: "100%",
        }}>
          <SquareIcon.Variants.HorizontalLayout 
            containerSize={50}
            iconSize={25}
            selected={studentCtx?.layout === LayoutType.HorizontalSquare}
            onClick={() => onLayoutChange(LayoutType.HorizontalSquare)}
          />
          <SquareIcon.Variants.VerticalLayout 
            containerSize={50}
            iconSize={25}
            selected={studentCtx?.layout === LayoutType.VerticalSquare}
            onClick={() => onLayoutChange(LayoutType.VerticalSquare)}
          />
          <SquareIcon.Variants.GridLayout 
            containerSize={50}
            iconSize={25}
            selected={studentCtx?.layout === LayoutType.Square}
            onClick={() => onLayoutChange(LayoutType.Square)}
          />
        </div>
      );
    }

    const center: React.ReactNode[] = [];
    const renderCameraButton = () => {
      const permitted = !(session.permissions?.video === false);
      const currRef = createRef<HTMLDivElement>();
      let hoverMessage: string | undefined = undefined;
      if (!permitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre caméra";
      return (
        <div ref={currRef} key="student-dashboard-overlay-camera-btn">
          <CircleIcon.Variants.Camera
            unavailable={!permitted}
            disabled={!studentCtx.videoStreamEnabled}
            onClick={async () => {
              if (!focusedMeetingAPI) return;
              const manager = focusedMeetingAPI.emissionManager;
              let success = true;
              if (manager.videoIsEnabled("cameras")) {
                success = (await focusedMeetingAPI.disableVideo({ direction: "outgoing", group: "cameras" })).success;
              } else {
                success = (await focusedMeetingAPI.enableVideo({ direction: "outgoing", group: "cameras" })).success;
              }
              if (!success) toastError("Une erreur est survenue...");
              studentCtx.setVideoStreamEnabled(manager.videoIsEnabled("cameras"));
            }}
          />
          {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
        </div>
      );
    };
    const renderAudioButton = () => {
      const currRef = createRef<HTMLDivElement>();
      let hoverMessage: string | undefined = undefined;
      if (!audioPermitted) hoverMessage = "Vous ne pouvez actuellement pas activer votre micro";
      return (
        <div ref={currRef} key="student-dashboard-overlay-microphone-btn">
          <CircleIcon.Variants.Mic
            unavailable={!audioPermitted}
            disabled={!studentCtx.audioStreamEnabled}
            onClick={async () => {
              if (!focusedMeetingAPI) return;
              const manager = focusedMeetingAPI.emissionManager;
              let success = true;
              if (manager.audioIsEnabled("cameras")) {
                success = (await focusedMeetingAPI.mute({ direction: "outgoing", group: "cameras" })).success;
              } else {
                success = (await focusedMeetingAPI.unmute({ direction: "outgoing", group: "cameras" })).success;
              }
              if (!success) toastError("Une erreur est survenue...");
              studentCtx.setAudioStreamEnabled(manager.audioIsEnabled("cameras"));
            }}
          />
          {hoverMessage ? <OverlayInformationMessage targetRef={currRef} message={hoverMessage} /> : null}
        </div>
      );
    };
    
    const renderPauseButton = () => {
      return (
        <CircleIcon.Variants.Pause
          onClick={() => updateMeetingState(MeetingState.PAUSED)}
        />
      );
    };

    const renderHangOffButton = () => {
      return (
        <CircleIcon.Variants.HangOff
          onClick={stop}
          style={{
            padding: "0.75em",
          }}
        />
      );
    };

    if (!isSpectator(session)) {
      if (isExpert(session)) {
        center.push(renderPauseButton());
        center.push(renderHangOffButton());
      }

      center.push(renderCameraButton());
      center.push(renderAudioButton());
    }

    const right: React.ReactNode[] = [
      <AppSoundSlider />,
      <MediaSourceViewer 
        sources={usableSources}
        meetingAPI={focusedMeetingAPI}
        onClick={() => setOverlayMode((v) => (v === "media" ? "none" : "media"))}
        selected={overlayMode === "media"}
      />, // shows local sources, screen / cameras
    ];
    
    return (
      <TopBar 
        style={{
          height: "unset",
          padding: "unset",
          backgroundColor: "transparent",
        }}
        left={left}
        center={center}
        right={right}
      />
    );
  }

  const renderUI = (children: React.ReactNode) => {
    return (
      <div style={{
        width: "100%",
        height: "100%",
        display: "flex",
      }}>
        {renderSideBar()}
        <div style={{
          height: "100%",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "1em",
          gap: "1em",
        }}>
          <div style={{
            width: "100%",
            flex: 1,
            minHeight: 0,
          }}>
            <div style={{
              display: "flex",
              height: "100%",
              gap: "1em",
            }}>
              {renderWorkBox()}
              <div style={{
                flex: 2,
                position: "relative",
              }}>
                {children}
                {renderOverlay()}
              </div>
            </div>
          </div>
          {renderBottomBar()}
        </div>
      </div>
    );
    // return (
    //   <StudentUI
    //     screens={renderPlayersDrawerContent()}
    //     title={meeting?.name ?? ""}
    //     date={new Date(meeting?.startedAt ?? new Date())}
    //     studentUser={{
    //       courseName,
    //       firstName: selfUser.identity?.firstname ?? "",
    //       lastName: selfUser.identity?.lastname ?? "",
    //       profilePicture: selfUser.identity?.avatar,
    //     }}
    //     expertUser={{
    //       courseName,
    //       firstName: expertUser?.firstname ?? "",
    //       lastName: expertUser?.lastname ?? "",
    //       profilePicture: expertUser?.avatar,
    //     }}
    //     selectedLayout={studentCtx.layout}
    //     selectedPage={toUIPage(pageContextRef.current?.page)}
    //     onLayoutChange={onLayoutChange}
    //     onPageChange={onPageChange}
    //     onQuit={() => pageContextRef.current?.goTo(PageMasterMode.LIST)}
    //     onChatClick={() => {
    //       setPopupMode(popupModeRef.current === "chat" ? "none" : "chat");
    //     }}
    //     chatButtonRef={chatButtonRef}
    //     onOpenSettings={() => {}}
    //     buttonsCenter={buttons}
    //     placeholderBottomRight={renderAllSelfPreviews()}
    //     dockedContentBottomRight={renderUIBottomRightDock()}
    //     enabledLayouts={[LayoutType.Square, LayoutType.HorizontalSquare, LayoutType.VerticalSquare]}
    //     enabledPages={getEnabledUIPages()}
    //     chatBadgeValue={chatBadgeValue}
    //     disableSettingsButton
    //     forceOpenBottomBar
    //   >
    //     {children}
    //   </StudentUI>
    // );
  };

  // if (meeting.state === MeetingState.STOPPED && !isExpert(session)) {
  //   return (
  //     <div className={styles.mainContainer}>
  //       <div className={styles.content} style={{ justifyContent: "center", alignItems: "center" }}>
  //         <div style={{ color: "white", marginBottom: "20px", fontSize: "1.5rem", fontWeight: "bold" }}>Le cours est terminé</div>
  //         <Button
  //           style={{ backgroundColor: "#0090FF", color: "white" }}
  //           title={"Retour à l'accueil"}
  //           onPress={() => {
  //             pageContextRef.current?.goTo(PageMasterMode.LIST);
  //           }}
  //         />
  //       </div>
  //     </div>
  //   );
  // }

  const renderHighlightBanner = () => {
    const highlightView = availableViews.find((view) => view.type === "highlight");

    if (!highlightView || !session) {
      return null;
    }

    const mySessionData = highlightView.students.find((s) => s.video && s.session.id === session.id);

    let otherGroupsNames = highlightView.students
      .filter(
        (data) => data.video && data.session.user.group?.room.status === MeetingRoomStatus.Ongoing && data.session.user.group.id !== mySessionData?.session.user.group?.id
      )
      .map((data) => data.session.user.group?.room.name);
    otherGroupsNames = Array.from(new Set(otherGroupsNames));

    let you = false;
    let plural = false;

    let message = "";

    if (mySessionData) {
      if (mySessionData.session.user.group?.room.status === MeetingRoomStatus.Ongoing) {
        message = "Votre groupe";
      } else {
        message = "Vous";
        you = true;
      }
    }

    /**
     * Either :
     * - there is atleast one group and we display group names only, ignoring users
     * - there is no group and we display the users number
     */
    if (otherGroupsNames.length === 1) {
      if (message) {
        message += " et le groupe ";
        plural = true;
      } else {
        message = "Le groupe ";
      }
      message += otherGroupsNames[0];
    } else if (otherGroupsNames.length > 1) {
      if (message) {
        message += " et les groupes ";
      } else {
        message = "Les groupes ";
      }
      message += otherGroupsNames.join(", ");
    } else {
      // no other groups

      // users that are not in the same room as me
      const otherSessions = highlightView.students.filter(
        (s) => s.video && s.session.id !== mySessionData?.session.id && (!s.session.user.group || s.session.user.group?.id !== mySessionData?.session.user.group?.id)
      );
      const remaining = otherSessions.length;

      if (remaining === 1) {
        if (message) {
          message += " et 1 autre utilisateur";
          plural = true;
        } else {
          message = "1 utilisateur";
        }
      } else if (remaining > 1) {
        plural = true;
        if (message) {
          message += ` et ${remaining} autres utilisateurs`;
        } else {
          message = `${remaining} utilisateurs`;
        }
      }
    }

    if (message) {
      if (you) {
        message += " êtes au tableau";
      } else {
        if (plural) {
          message += " sont au tableau";
        } else {
          message += " est au tableau";
        }
      }
    } else {
      message = "Préparation du tableau ...";
    }

    const isOnHighlightPage = pageContextRef.current?.page === PageMasterMode.MEETING_HIGHLIGHT;

    return (
      <div
        onClick={() => {
          if (!isOnHighlightPage) {
            pageContextRef.current?.goTo(PageMasterMode.MEETING_HIGHLIGHT, { id: pageContextRef.current?.params?.id });
          }
        }}
        style={{
          display: "flex",
          gap: "1em",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          backgroundColor: "#0090FF",
          opacity: isOnHighlightPage ? 1 : 0.5,
          cursor: isOnHighlightPage ? "default" : "pointer",
          padding: "0.5em",
        }}
      >
        <div
          style={{
            color: "white",
            fontWeight: "bold",
          }}
        >
          {message.toUpperCase()}
        </div>
        {isOnHighlightPage || (
          <div
            style={{
              color: "white",
            }}
          >
            (Revenir)
          </div>
        )}
      </div>
    );
  };

  const renderActivityPopup = () =>
    activityData && session && <MeetingActivityJoinPopup activityData={activityData} meetingId={meeting.id} selfSessionId={session.id} />;

  const renderBroadcastingExpertBanner = () => {
    if (!broadcastingExpertSessions.length) return null;
    let expertSessions = 0;
    let pedagogySessions = 0;
    broadcastingExpertSessions.forEach((s) => {
      if (s.session?.extra?.isPedagogy) pedagogySessions++;
      else expertSessions++;
    });
    let message = "";
    if (pedagogySessions) {
      if (pedagogySessions > 1) {
        message = "Des ingénieurs pédagogiques";
      } else {
        message = "Un ingénieur pédagogique";
      }
    }
    if (expertSessions) {
      if (message.length) message += " et ";
      if (expertSessions > 1) {
        message += `${message.length ? "des" : "Des"} experts`;
      } else {
        message += `${message.length ? "un" : "Un"} expert`;
      }
    }
    message += ` ${broadcastingExpertSessions.length > 1 ? "s'adressent" : "s'adresse"} à tout le monde`;
    return (
      <div
        className={styles.broadcastingExpertIndicator}
        style={{
          backgroundColor: "#FF0000",
          padding: "0.1em 3em",
          fontSize: "1.25em",
          fontWeight: 500,
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "0.25em",
          whiteSpace: "nowrap",
        }}
      >
        <ReactIcons.PedagoHeadphone2 style={{ height: "1em" }} />
        {message}
      </div>
    );
  };

  const renderDebugOutgoingStream = () => {
    if (!settings?.debug?.displayOutgoingStreamStats && !settings?.debug?.displaySessionIds) return null;
    return (
      <BottomPanelWebrtcSessionStats
        incomingSessions={settings?.debug?.displayIncomingStreamStats ? receptionSessions : undefined}
        outgoingSessions={settings?.debug?.displayOutgoingStreamStats ? emissionSessions : undefined}
        sessions={(emissionSessions ?? []).map((s, i) => ({
          id: s.getId(),
          name: `${s.data?.mediaSession?.name ?? `ouput n°${i}`}`,
          session: settings?.debug?.displayOutgoingStreamStats ? s : undefined,
          meeting: settings?.debug?.displaySessionIds ? { session: session, mediaSession: s.data?.mediaSession } : undefined,
        }))}
      />
    );
  };

  return (
    <MasterUI>
      <PauseView
        meeting={meeting}
        onResume={() => updateMeetingState(MeetingState.RUNNING)}
        enabled={isExpert(session)}
      >
        <div className={styles.mainContainer}>
          {isSpectator(session) ? <div className={styles.spectatorBar}>Spectateur</div> : null}
          <div className={styles.content}>
            {renderUI(
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  height: "100%",
                }}
              >
                {renderHighlightBanner()}
                <div style={{ flex: 1, minHeight: 0, width: "100%" }}>
                  <Outlet />
                </div>
                {renderActivityPopup()}
                {renderBroadcastingExpertBanner()}
                <DevSoundIndicatorsBanner meetingData={populatedMeetingData} canHearSession={(session) => canHearSession(focusedMeetingAPI, session)} />
              </div>
            )}
          </div>
          {renderDebugOutgoingStream()}
        </div>
      </PauseView>
    </MasterUI>
  );
};
